import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { CommentsField } from "@/shared/components/CommentsField";
import { DelayAreaSelector } from "@/shared/selectors/DelayAreaSelector";
import { DelayCategorySelector } from "@/shared/selectors/DelayCategorySelector";
import { DelayCodeTreePicker } from "@/shared/selectors/DelayCodeTreePicker";
import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	MesfModal,
	TimeService,
	useShiftNavigator,
	useUserContext,
} from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { capitalize } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useDelayById } from "../../DelaysManagerPage";
import { DelayByShift } from "../../models/DelayByShift";
import {
	justifyDelay,
	upsertDelay,
} from "../../repositories/DelaysManagerRepository";

const moment = getMomentTz();

type Props = {
	showModal: "new" | "edit" | "delete" | "justify" | "split" | "";
	onHide: (shouldUpdate: boolean) => void;
	delayId: number | null;
	assetId?: number;
	suffixTitle?: string;
};

export const UpsertJustifyDelayModal = ({
	showModal,
	onHide,
	delayId,
	assetId,
	suffixTitle = "Delays Manager",
}: Props) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [delay, setDelay] = useState<DelayByShift | null>(null);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [title, setTitle] = useState("");
	const [duration, setDuration] = useState<string | number>("");
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	// When filter is "Mill" (parent, 10), pre-fill with the header area selector
	const effectiveAssetId =
		assetId === 10 ? Number(defaultAreaId) || assetId : assetId;

	const {
		data: delays,
		isLoading,
		isSuccess,
	} = useDelayById({
		delayId: delayId,
		onError: (e) => setError(e.message),
	});

	const { register, setValue, control, handleSubmit, getValues, reset, watch } =
		useForm<DelayByShift>({
			defaultValues: {},
		});

	const start = watch("Start");
	const end = watch("End");

	const onSubmit: SubmitHandler<DelayByShift> = async (data: DelayByShift) => {
		setIsSubmitLoading(true);
		if (showModal === "justify") {
			const response = await justifyDelay(
				data.DelayId!,
				data.DelayCodeId ?? null,
				data.DelayCategoryId ?? null,
				data.Comments ?? null,
			);
			if (response.ok) {
				setNotificationMessage("Delay was justified successfully");
				onHide(true);
			} else {
				setError(response.message);
			}
		} else {
			const response = await upsertDelay(
				data.DelayId === 0 ? null : data.DelayId,
				data.AssetId,
				data.Start,
				data.End,
				data.DelayCodeId,
				data.DelayCategoryId,
				data.Comments,
			);
			if (response.ok) {
				setNotificationMessage(
					"Delay was " +
						(showModal == "new" ? "created" : "edited") +
						" successfully",
				);

				// Navigate to new delay
				// const startMoment = moment.isMoment(data.Start)
				//   ? data.Start.toISOString()
				//   : moment(data.Start).toISOString();
				// const utcMoment = moment.utc(startMoment);
				// const formattedStart = utcMoment.toDate();
				// await getShiftDataFromAPI(formattedStart, 0);
				onHide(true);
			} else {
				setError(response.message);
			}
		}
		setIsSubmitLoading(false);
		onHide(false);
	};

	useEffect(() => {
		if (showModal !== "" && showModal !== "delete") {
			setIsSubmitLoading(false);
			setTitle(`${showModal.toUpperCase()} DELAY`);

			const defaultStart = shiftInfo?.CurrentStart || null;
			const defaultEnd = new Date(shiftInfo?.CurrentStart!) || null;
			defaultEnd.setHours(defaultEnd.getHours() + 1);

			if (delay && showModal !== "new") {
				setValue("DelayCodeId", delay.DelayCodeId);
				setValue("DelayId", delay.DelayId);
				setValue(
					"Start",
					delay.Start === null || delay.Start === undefined
						? defaultStart
						: delay.Start,
				);
				setValue(
					"End",
					delay.End === null || delay.End === undefined ? null : delay.End,
				);
				setValue("DelayCategoryId", delay.DelayCategoryId);
				setValue("Comments", delay.Comments);
				setValue("AssetId", delay.AssetId);
			} else {
				reset();
				setValue("Start", defaultStart);
				setValue("End", defaultEnd);
				setValue("AssetId", effectiveAssetId!);
			}
		} else {
			reset();
		}
	}, [showModal, delay]);

	useEffect(() => {
		if (start && end) {
			const duration = moment.duration(moment(end).diff(moment(start)));
			const minutes = Math.floor(duration.asMinutes());
			const seconds = duration.seconds();
			setDuration(`${minutes}m ${seconds}s`);
		} else {
			setDuration("");
		}
	}, [start, end]);

	useEffect(() => {
		if (isSuccess) {
			if (delays && delays[0]) {
				setDelay(delays[0]);
			}
		}
	}, [isSuccess, delays]);

	return (
		<>
			{(showModal === "justify" ||
				showModal === "edit" ||
				showModal === "new") && (
				<HelmetDexteel
					title={`${capitalize(showModal)} Delay - ${suffixTitle}`}
				/>
			)}
			<MesfModal
				maxWidth="md"
				open={showModal !== "" && showModal !== "delete"}
				handleClose={() => {
					onHide(false);
				}}
				id="upsertDelay"
				title={title}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
						{isLoading && <CenteredLazyLoading />}
						{!isLoading && (
							<>
								<Grid2 container spacing={1} style={{ alignItems: "center" }}>
									<Grid2 size={{ xs: 4, md: 3.5 }}>
										<Box style={{ height: 35 }}>
											<Controller
												name="Start"
												control={control}
												rules={{
													deps: ["End"],
													required: "Start date is required",
													validate: {
														notAfterEnd: (value) => {
															const endDate = watch("End");
															if (value && endDate && value > endDate) {
																return "Start date cannot be after End date.";
															}
															return true;
														},
														notInFuture: (value) => {
															if (value && value > new Date()) {
																return "Start date cannot be in the future.";
															}
															return true;
														},
													},
												}}
												render={({ field, fieldState: { error } }) => (
													<DateTimePicker
														disabled={showModal === "justify"}
														label="Delay Start"
														timezone={TimeService.getInstance().getServerTimeZone()}
														format="MM/DD/YYYY HH:mm:ss"
														ampm={false}
														value={field.value ? moment(field.value) : null}
														onChange={field.onChange}
														slotProps={{
															textField: {
																size: "small",
																margin: "dense",
																error: !!error,
																helperText: error?.message,
															},
														}}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 4, md: 3.5 }}>
										<Box style={{ height: 35 }}>
											<Controller
												name="End"
												control={control}
												rules={{
													deps: ["Start"],
													validate: {
														notBeforeStart: (value) => {
															const startDate = watch("Start");
															if (value && startDate && value < startDate) {
																return "End date cannot be before Start date.";
															}
															return true;
														},
														notInFuture: (value) => {
															if (value && value > new Date()) {
																return "End date cannot be in the future.";
															}
															return true;
														},
														notSameAsStart: (value) => {
															const startDate = watch("Start");
															if (
																value &&
																startDate &&
																moment(value).isSame(moment(startDate))
															) {
																return "End date cannot be the same as Start date.";
															}
															return true;
														},
													},
												}}
												render={({ field, fieldState: { error } }) => (
													<DateTimePicker
														disabled={showModal === "justify"}
														label="Delay End"
														format="MM/DD/YYYY HH:mm:ss"
														ampm={false}
														timezone={TimeService.getInstance().getServerTimeZone()}
														value={field.value ? moment(field.value) : null}
														onChange={field.onChange}
														slotProps={{
															textField: {
																size: "small",
																margin: "dense",
																error: !!error,
																helperText: error?.message,
															},
														}}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 4, md: 2.5 }} marginTop={1.3}>
										<Box style={{ height: 45 }}>
											<TextField
												label="Duration (min)"
												disabled
												value={duration}
												margin="dense"
												autoComplete="off"
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 4, md: 2.5 }}>
										<Box style={{ height: 35 }}>
											<Controller
												name="AssetId"
												control={control}
												rules={{ required: "Asset is required" }}
												render={({ field, fieldState: { error } }) => (
													<DelayAreaSelector
														label="Asset"
														value={field.value}
														onChange={(newValue: number) => {
															field.onChange(newValue);
															setValue("DelayCodeId", null);
															setValue("DelayCodeName", null);
															setValue("DelayCategoryId", null);
														}}
														fieldError={error}
														disabled={showModal !== "new"}
														required
													/>
												)}
											/>
										</Box>
									</Grid2>
								</Grid2>
								<Grid2 container spacing={1} style={{ alignItems: "center" }}>
									<Grid2 size={{ xs: 4, md: 8 }} marginTop={0.8}>
										<Box style={{ height: 58 }}>
											<Controller
												name="DelayCodeId"
												control={control}
												render={({ field }) => (
													<DelayCodeTreePicker
														assetId={getValues("AssetId")}
														value={field.value}
														onChange={(value, defaultCategoryId) => {
															field.onChange(value);
															setValue("DelayCategoryId", defaultCategoryId);
														}}
														showFullPath
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 4, md: 4 }}>
										<Box style={{ height: 45 }}>
											<Controller
												name="DelayCategoryId"
												control={control}
												render={({ field, fieldState: { error } }) => (
													<DelayCategorySelector
														label="Category"
														value={field.value}
														onChange={field.onChange}
														fieldError={error}
														assetId={watch("AssetId")}
														disabled={!watch("AssetId")}
													/>
												)}
											/>
										</Box>
									</Grid2>
								</Grid2>
								<Grid2 container spacing={1} style={{ alignItems: "center" }}>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<Controller
											name="Comments"
											control={control}
											render={({ field, fieldState: { error } }) => (
												<CommentsField
													value={field.value}
													onChange={field.onChange}
													fieldError={error}
													getValues={getValues}
													rows={{ minRows: 3, maxRows: 3 }}
													maxLength={500}
												/>
											)}
										/>
									</Grid2>
								</Grid2>
								<Grid2 size={{ xs: 6, md: 6 }}>
									<FormControlLabel
										control={
											<Checkbox
												{...register("IsDelayManual")}
												color="primary"
												checked={showModal === "new" || delay?.IsDelayManual}
												disabled={true}
											/>
										}
										label="Manual Delay"
									/>
								</Grid2>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 20px" }}>
						<FooterModalButtons
							isSubmitLoading={isSubmitLoading}
							onHide={onHide}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
